/**
 * @file InlineEdit.tsx.
 *
 * @summary Text that turns into an input on click, or Enter/Space when focused.
 *
 * Not a shadcn component. Custom built on Base UI so it composes via `render`.
 *
 *   - Enter or blur commits, Escape cancels.
 *   - The committed value shows optimistically until `value` changes. If
 *     `onValueCommit` returns a promise that rejects, it reverts instead. A
 *     commit that never changes `value` stays pending, so make it reject.
 *   - With `required`, an empty value reverts instead of committing.
 *   - Disabled renders plain text in a `div`.
 */

import { useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { Input as InputPrimitive } from '@base-ui/react/input'
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cn } from 'tailwind-variants'

type InlineEditProps = Omit<
  useRender.ComponentProps<'button'>,
  'value' | 'defaultValue' | 'onChange'
> & {
  value: string
  onValueCommit: (value: string) => Promise<void> | void
  placeholder?: string
  disabled?: boolean
  required?: boolean
}

/**
 * Shared by the preview and the input, so padding and focus ring match.
 * The ring is inset because this usually sits inside a clipping container.
 */
const BASE_CLASS =
  'px-1 text-left rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50'

/**
 * Stable ref callback, so it only runs when the input mounts
 */
function focusAndSelect(input: HTMLInputElement | null) {
  input?.focus({ preventScroll: true })
  input?.select()
}

function InlineEdit({
  value,
  onValueCommit,
  placeholder,
  disabled = false,
  required = false,
  className,
  render,
  ...props
}: InlineEditProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  // The committed draft, plus the `value` it replaces. It shows until `value`
  // changes (the store caught up) or the commit rejects. Resolving is not
  // enough: the store can update after the request settles.
  const [pendingCommit, setPendingCommit] = useState<{
    draft: string
    base: string
  } | null>(null)
  // Enter/Escape unmount the input, which can fire a trailing blur.
  // This ref makes sure a single edit only finishes once. It resets when the
  // input gets focus, so render never touches it.
  const finishedRef = useRef(false)

  // `value` moved on, so the pending commit is stale. Reset during render
  // (not in an effect), so it can not resurface if `value` returns to `base`.
  if (pendingCommit && pendingCommit.base !== value) {
    setPendingCommit(null)
  }

  const pending = pendingCommit !== null && pendingCommit.base === value
  const displayValue = pending ? pendingCommit.draft : value

  function startEditing() {
    if (disabled || pending) return
    setDraft(displayValue)
    setEditing(true)
  }

  function cancel() {
    if (finishedRef.current) return
    finishedRef.current = true
    setEditing(false)
  }

  async function commit() {
    if (finishedRef.current) return
    finishedRef.current = true
    setEditing(false)

    if (draft === value) return
    if (required && !draft.trim()) return

    setPendingCommit({ draft, base: value })
    try {
      await onValueCommit(draft)
    } catch {
      // Fall back to `value` (the last known good one).
      setPendingCommit(null)
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      void commit()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      cancel()
    }
  }

  const preview = useRender({
    defaultTagName: disabled ? 'div' : 'button',
    render,
    props: mergeProps<'button'>(
      disabled
        ? {}
        : {
            type: 'button',
            onClick: startEditing,
          },
      {
        className: cn(
          BASE_CLASS,
          !disabled && 'cursor-text',
          pending &&
            'bg-animated from-transparent to-white/20 pointer-events-none',
          className,
        ),
        children: displayValue || (
          <span className="opacity-0 group-hover:opacity-40 transition-opacity">
            {placeholder}
          </span>
        ),
      },
      props,
    ),
    state: {
      slot: 'inline-edit',
      disabled,
      pending,
      empty: !displayValue,
    },
  })

  if (!editing) return preview

  return (
    <InputPrimitive
      data-slot="inline-edit"
      data-editing=""
      ref={focusAndSelect}
      className={cn(BASE_CLASS, 'w-full min-w-0 bg-transparent', className)}
      value={draft}
      placeholder={placeholder}
      required={required}
      onValueChange={setDraft}
      onFocus={() => {
        finishedRef.current = false
      }}
      onBlur={() => void commit()}
      onKeyDown={onKeyDown}
    />
  )
}

export { InlineEdit }
export type { InlineEditProps }
